import styled from "@emotion/styled"
import { css } from "@emotion/react"
import Typography from "@mui/material/Typography"
import { Link } from "utils"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"
import { typographyVariants } from "../../theme/typography"

/* ------------------------------------------------------------------ */
/* Card — contained white card. Mobile: column (media on top).         */
/* Desktop: row (media + content side by side).                        */
/*                                                                     */
/* RTL note: `flex-direction: row` is reversed by the browser in RTL,  */
/* so JSX source order is authored as [Content, Media] to render the   */
/* default visual order [media LEFT, content RIGHT] (image at x=0,     */
/* content at the start/right) — matching the Figma desktop reference. */
/* `$imageOnRight` toggles row-reverse to swap sides; because the      */
/* browser already mirrors row in RTL, row-reverse mirrors it back, so */
/* the boolean reads consistently as "image on the visual end" in      */
/* both directions.                                                    */
/* No stroke/shadow captured on the card node (walker:                 */
/* visibleEffectNodeCount=0) — card is intentionally borderless.       */
/* ------------------------------------------------------------------ */
export const Card = styled.div<{ $imageOnRight: boolean; $animated: boolean }>`
  width: 100%;
  display: flex;
  /* Mobile is media-on-top / content-below (figma-mobile.png). JSX source
     order is [Content, Media] for the desktop RTL row trick; the vertical
     (block) axis is NOT mirrored by RTL, so column-reverse is needed to lift
     Media above Content on mobile. */
  flex-direction: column-reverse;
  gap: ${sizes.spacing[40]}px;
  /* Horizontal insets come from the wrapping <Container> (section padding +
     max-width). The card only owns its vertical padding — adding horizontal
     padding here too would double-inset and squeeze the row (Figma's 120px
     side padding IS the Container's desktop inset, not an extra card pad). */
  padding-block: ${sizes.spacing[48]}px;
  background-color: ${colors.light.bg.primary};
  border-radius: ${sizes.borderRadius[24]}px;

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: ${(p) => (p.$imageOnRight ? "row-reverse" : "row")};
    align-items: center;
    gap: ${sizes.spacing[64]}px;
    padding-block: ${sizes.spacing[64]}px;
    border-radius: ${sizes.borderRadius[40]}px;
  }

  /* EGDE-318 parallax: while the media is wider than its resting size the
     content column is pushed past the card edge (Figma "Medium" state shows
     the text clipped mid-entry) — the card clips it.

     The flex line must START at the media's edge so the growing media pushes
     the content out past the opposite (END) edge — Figma's transition frame
     anchors the image at its own side and clips the text at the far edge.
     With the default row the line starts at the CONTENT (RTL: content is
     laid at the right first and the media overflows uselessly past the left
     edge instead). Reversing the direction + explicit order (media first,
     content second — see Media/Content) anchors the media at its visual side
     in BOTH directions, and mirrors automatically like the static trick. */
  ${(p) =>
    p.$animated &&
    css`
      overflow: hidden;

      @media (min-width: ${breakpoints.lg}px) {
        flex-direction: ${p.$imageOnRight ? "row" : "row-reverse"};
        /* EGDE-510 point A1 (Raz, 2026-07-29): "The text ... enters from inside
           the container when the image is stretched edge to edge. The text
           should enter from the right edge of the screen."
           The copy's travel already begins outside the screen (measured at
           1440: it starts at x=1384 with a 596px box, so its RTL leading edge
           is at 1980 — well past the viewport) but the card clipped it back to
           its own edge, so the copy only ever appeared from x=1320 inwards:
           from inside the container, exactly as reported. Clipping now happens
           at the viewport instead (see Bleed), so the copy is visible for the
           whole run in from the screen edge. */
        overflow: visible;
      }
    `}
`

/* ------------------------------------------------------------------ */
/* Bleed — viewport-level clip for the animated copy's travel.         */
/*                                                                     */
/* The Card can no longer clip the copy (point A1 above), so something  */
/* has to, or the copy parked off-screen would add horizontal page      */
/* overflow and a stray scrollbar. This wrapper is full-width, so it    */
/* clips exactly at the screen edges — the copy becomes visible the     */
/* instant it crosses into the viewport. `clip` rather than `hidden`    */
/* so it never becomes a scroll container, and x-only so sticky/shadow  */
/* behaviour on the block axis is untouched.                           */
/* ------------------------------------------------------------------ */
export const Bleed = styled.div`
  width: 100%;
  overflow-x: clip;
`

/* ------------------------------------------------------------------ */
/* Media — square (aspect-ratio 1). Holds <Image> or <video>.          */
/* ------------------------------------------------------------------ */
export const Media = styled.div<{ $animated?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 343px;
  aspect-ratio: 1 / 1;
  border-radius: ${sizes.borderRadius[24]}px;
  overflow: hidden;
  flex-shrink: 0;

  @media (min-width: ${breakpoints.lg}px) {
    width: 540px;
    max-width: 540px;
    border-radius: ${sizes.borderRadius[40]}px;
  }

  /* EGDE-318 scroll-scrubbed parallax (--scrub 0→1 set on the Card):
     Mobile — the image starts as a small thumbnail (~143/343, cf. the
     PowerNumbers "Transition" beat) and grows to full size with the scroll.
     A transform keeps the layout box fixed, so the card height never changes
     mid-scrub (a height change would feed back into the scroll position).
     Desktop — Figma "Large" state spreads the image across the ENTIRE card;
     it shrinks to the resting 540px square as the user scrolls. Width is
     animated (not transform) because the content column must be pushed out
     of the card by the wide image and slide in as it narrows. */
  ${(p) =>
    p.$animated &&
    css`
      transform: scale(calc(0.42 + 0.58 * var(--scrub, 0)));
      transform-origin: top center;

      @media (min-width: ${breakpoints.lg}px) {
        transform: none;
        aspect-ratio: auto;
        height: 540px;
        width: calc(100% - (100% - 540px) * var(--scrub, 0));
        max-width: none;
        /* First in the reversed flex line — anchored at the card's media
           edge, so its growth pushes the content out the far edge. */
        order: 1;
      }

      @media (prefers-reduced-motion: reduce) {
        transform: none;

        @media (min-width: ${breakpoints.lg}px) {
          width: 540px;
        }
      }
    `}
`

/* ------------------------------------------------------------------ */
/* Video element — fills the media square, object-fit cover.           */
/* ------------------------------------------------------------------ */
export const MediaVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

/* ------------------------------------------------------------------ */
/* Content column — title group + CTA. Right-aligned in RTL via        */
/* text-align:start / align-items:flex-start.                          */
/* ------------------------------------------------------------------ */
export const Content = styled.div<{ $animated?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[40]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    flex: 1;
  }

  /* EGDE-318 parallax:
     Mobile — text fades/rises in with the scroll (image grows above it).
     Desktop — the column keeps its RESTING width (flex-basis fixed to the
     space left of the 540px image + 64px gap) instead of flex:1, so while
     the image is wide the whole column sits past the card edge (clipped by
     the Card) and slides into place as the image shrinks. The slide is
     layout-driven, so RTL/LTR direction is correct by construction. */
  ${(p) =>
    p.$animated &&
    css`
      opacity: var(--scrub, 0);
      transform: translateY(calc(24px * (1 - var(--scrub, 0))));

      @media (min-width: ${breakpoints.lg}px) {
        flex: 0 0 calc(100% - ${540 + sizes.spacing[64]}px);
        min-width: 0;
        /* EGDE-510 point A1 (Raz, 2026-07-29): "The text is not correct; it
           enters from inside the container when the image is stretched edge to
           edge. The text should enter from the right edge of the screen."

           History, because this line has been wrong twice in opposite
           directions: r2 held the copy transparent until the final stretch so
           it would land WITHIN the card; QA then filed "missing the text
           entrance animation" (point 9), so r3 added a 24px lift over the same
           late window. Both kept the copy hidden for the part of the travel
           that Raz actually wants to SEE — measured, it stayed at opacity 0
           until x≈852, deep inside a card spanning 120→1320.

           The travel itself was always right: it is layout-driven (the
           shrinking media pushes the column) and so mirrors correctly in
           LTR/RTL by construction. It just has to be visible. The copy is now
           opaque for the whole run, entering at the screen edge and settling
           into its seat as the image narrows. No translateY: the horizontal
           travel IS the entrance, and a lift on top of it read as a wobble. */
        opacity: 1;
        transform: none;
        /* Second in the reversed flex line (after the media). */
        order: 2;
      }

      @media (prefers-reduced-motion: reduce) {
        opacity: 1;
        transform: none;
      }
    `}
`

/* ------------------------------------------------------------------ */
/* Title group — chevron+title row, then description.                  */
/* ------------------------------------------------------------------ */
export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[8]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[16]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Title row — decorative gradient chevron inline before the title.    */
/* RTL: source [Chevron, Title] renders [Title left, Chevron right],   */
/* so the chevron sits at the start (right in RTL) of the title.       */
/* ------------------------------------------------------------------ */
export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizes.spacing[6]}px;
  width: 100%;
`

/* Chevron wrapper — fixed size, never grows (decorative icon).        */
/* Sizes the inner SVG: 16x27 mobile, 21x36 desktop.                   */
export const ChevronWrapper = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  /* Match the title's first-line line-height so the chevron stays optically
     centered against the FIRST line when the title wraps (TitleRow pins it to
     the top via align-items: flex-start). Sourced from the H2 token so it
     tracks any future typography retune. */
  height: ${typographyVariants.h2.mobile.lineHeight}px;

  & svg {
    width: 16px;
    height: 27px;
  }

  @media (min-width: ${breakpoints.lg}px) {
    height: ${typographyVariants.h2.desktop.lineHeight}px;

    & svg {
      width: 21px;
      height: 36px;
    }
  }

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

/* ------------------------------------------------------------------ */
/* Title — H2 Bold, right-aligned in RTL.                              */
/* ------------------------------------------------------------------ */
export const Title = styled(Typography)`
  color: ${colors.light.text.primary};
  text-align: start;

  /* EGDE-501 r2: the card title IS the standard 32px mobile h2 (Figma
     dev-mode node 40012505-35792) — the design keeps it to one line via an
     editorial character limit (max 24), not a smaller font. The earlier 28px
     override compensated for the pre-Almoni fallback font's wider metrics
     and no longer matches the design. */
`

/* ------------------------------------------------------------------ */
/* Description — Body 1 Regular, right-aligned in RTL.                 */
/* preserves authored line breaks.                                     */
/* ------------------------------------------------------------------ */
export const Description = styled(Typography)`
  color: ${colors.light.text.primary};
  text-align: start;
  letter-spacing: 0.15px;
  white-space: pre-line;
`

/* ------------------------------------------------------------------ */
/* CTA pill — primary gradient button (EGDE-501, superseding the EGDE-324  */
/* white/bordered secondary look): the Figma card CTA rests on the CTA     */
/* gradient with a dark label. Hover adds the "Strip White" glow only —    */
/* the gradient background never transitions (cf. EGDE-355).               */
/* ------------------------------------------------------------------ */
const ctaBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  box-sizing: border-box;
  /* EGDE-626: 200x46 is the RESTING size from Figma, not a cap. As a fixed
     width/height the label simply ran out of the pill on both breakpoints —
     every other CTA in the theme already sizes with min-width, so this one was
     the outlier. The max-width keeps a very long label inside the card (the
     mobile column is only 343px) by wrapping it instead of overflowing. */
  width: auto;
  min-width: 200px;
  max-width: 100%;
  min-height: 46px;
  padding: ${sizes.spacing[12]}px ${sizes.spacing[16]}px;
  border: none;
  border-radius: ${sizes.borderRadius.full}px;
  background: ${colors.gradients.gradientCta};
  text-decoration: none;
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  /* EGDE-500: Main CTA sheet — primary hovers to the flat orange with the
     button glow and presses to the darker orange. The gradient↔solid swap is
     instant (cf. EGDE-355: not interpolable). */
  &:hover,
  &:focus-visible {
    background: ${colors.primitives.primary500};
    box-shadow: ${colors.shadows.serviceCta};
  }

  &:active {
    background: ${colors.primitives.primary600};
    box-shadow: none;
  }

  @media (min-width: ${breakpoints.lg}px) {
    min-height: 52px;
  }
`

export const CtaLink = styled(Link)`
  ${ctaBase}
`

export const CtaAnchor = styled.a`
  ${ctaBase}
`

/* CTA label — Body 1 Bold. */
export const CtaText = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
  /* EGDE-626: white-space nowrap here is what pushed a long label past the
     pill's edge. The pill grows with the label now, and once it hits the card
     width the label wraps and the pill grows taller instead of spilling out. */
  text-align: center;
  overflow-wrap: anywhere;
`
